import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/Header';
import { IconDownload } from '@/components/Icons';
import { AVAILABLE_LOCALES, detectLocale, localeInfo, useT } from '@/i18n';
import { cx } from '@/lib/cx';
import { formatBytes } from '@/lib/format';
import { refreshAll } from '@/market/quoteService';
import { settingsStore, useSettings } from '@/settings/settingsStore';
import { appStore, useAppState } from '@/state/appStore';
import { repo } from '@/storage/db';
import { DISPLAY_CURRENCIES, type Currency } from '@/types';
import { exportBackup, importBackup, pickBackupFile } from './backup';
import { CrashLog } from '@/components/CrashLog';
import { DataSources } from '@/components/DataSources';
import { KeyPanel } from './KeyPanel';
import s from './settings.module.css';

export function SettingsView() {
  const { t } = useT();
  const settings = useSettings();
  const app = useAppState();
  const [usage, setUsage] = useState<number | null>(null);

  useEffect(() => {
    void repo.estimateUsage().then(setUsage);
  }, [app.assets.length, app.snapshots.length]);

  const counts = { portfolios: app.portfolios.length, assets: app.assets.length, days: app.snapshots.length };

  const clearAll = async () => {
    if (!window.confirm(t('settings.confirmClear', counts))) return;
    await appStore.clearAll();
    appStore.notify(t('settings.cleared'));
  };

  const doImport = async () => {
    const file = await pickBackupFile();
    if (!file) return;
    try {
      await importBackup(file);
      void refreshAll(true);
    } catch (e) {
      appStore.notify(t('settings.importFailed', { error: (e as Error).message }));
    }
  };

  const fh = settings.data.finnhub;
  const mv = settings.data.massive;
  const td = settings.data.twelvedata;
  const keyBadge = (state: typeof fh, okLabel: string) => (
    <span className="badge"><i className={state?.status === 'ok' ? 'dot' : 'dot off'} />{state?.status === 'ok' ? okLabel : state?.status === 'invalid' ? t('settings.keyInvalid') : t('settings.keyNone')}</span>
  );

  return (
    <div className="page">
      <Header crumbs={[{ label: t('header.list'), to: '/' }]} title={t('settings.title')} showCurrency={false} />

      <section className={s.layout}>
        <nav className={s.nav}>
          <a href="#llm">{t('settings.navLLM')}</a>
          <a href="#data">{t('settings.navData')}</a>
          <a href="#display">{t('settings.navDisplay')}</a>
          <a href="#privacy">{t('settings.navPrivacy')}</a>
        </nav>

        <div>
          <div className={s.sec} id="llm">
            <h3 className={`serif ${s.h3}`}>{t('settings.navLLM')}</h3>
            <KeyPanel kind="llm" />
          </div>

          <div className={s.sec} id="data">
            <h3 className={`serif ${s.h3}`}>{t('settings.navData')}</h3>
            <DataSources />
            <div className={s.kcards}>
              <div className={s.kcard}>
                <div className={s.kcardHead}><span className="serif" style={{ fontSize: 18, fontWeight: 500 }}>Finnhub</span>{keyBadge(fh, t('settings.finnhubOk'))}</div>
                <KeyPanel kind="finnhub" compact />
                <span className="muted" style={{ fontSize: 12 }}>{t('settings.finnhubMeta')}<a href="https://finnhub.io/register" target="_blank" rel="noopener noreferrer">{t('settings.apply')}</a></span>
              </div>
              <div className={s.kcard}>
                <div className={s.kcardHead}><span className="serif" style={{ fontSize: 18, fontWeight: 500 }}>Massive Starter</span>{keyBadge(mv, t('settings.massiveOk'))}</div>
                <KeyPanel kind="massive" compact />
                <span className="muted" style={{ fontSize: 12 }}>{t('settings.massiveMeta')}<a href="https://massive.com/stocks" target="_blank" rel="noopener noreferrer">{t('settings.subscribe')}</a></span>
              </div>
              <div className={s.kcard}>
                <div className={s.kcardHead}><span className="serif" style={{ fontSize: 18, fontWeight: 500 }}>Twelve Data Grow</span>{keyBadge(td, t('settings.twelvedataOk'))}</div>
                <KeyPanel kind="twelvedata" compact />
                <span className="muted" style={{ fontSize: 12 }}>{t('settings.twelvedataMeta')}<a href="https://twelvedata.com/pricing" target="_blank" rel="noopener noreferrer">{t('settings.subscribe')}</a></span>
              </div>
            </div>
          </div>

          <div className={s.sec} id="display">
            <h3 className={`serif ${s.h3}`}>{t('settings.navDisplay')}</h3>
            <div className={s.kv}>
              <span className="label">{t('settings.language')}</span>
              <div className="seg">
                <button type="button" className={cx(!settings.locale && 'on')} onClick={() => settingsStore.setLocale(undefined)}>{t('settings.languageAuto', { detected: localeInfo(detectLocale()).label })}</button>
                {AVAILABLE_LOCALES.map((l) => <button key={l} type="button" className={cx(settings.locale === l && 'on')} onClick={() => settingsStore.setLocale(l)}>{localeInfo(l).label}</button>)}
              </div>
              <span className="label">{t('settings.currency')}</span>
              <div className="seg">
                {DISPLAY_CURRENCIES.map((c) => <button key={c} type="button" className={cx(settings.displayCurrency === c && 'on')} onClick={() => settingsStore.setDisplayCurrency(c as Currency)}>{c}</button>)}
              </div>
              <span className="label">{t('settings.fx')}</span>
              <span>{t('settings.fxText')}{app.fx ? t('settings.fxCurrent', { date: app.fx.date }) : ''} <span className="muted">{t('settings.costNote')}</span></span>
              <span className="label">{t('settings.refresh')}</span>
              <span>{t('settings.refreshText')} <button type="button" className="btn link" style={{ fontSize: 12 }} onClick={() => void refreshAll(true)}>{t('settings.refreshNow')}</button></span>
              <span className="label">{t('settings.change')}</span>
              <span>{t('settings.changeText')}<span className="muted">{t('settings.changeNote')}</span></span>
            </div>
          </div>

          <div className={s.sec} id="privacy">
            <h3 className={`serif ${s.h3}`}>{t('settings.navPrivacy')}</h3>
            <div className={s.dataRow}>
              <button type="button" className="btn quiet" onClick={() => void exportBackup()}><IconDownload size={13} />{t('settings.export')}</button>
              <button type="button" className="btn quiet" onClick={() => void doImport()}>{t('settings.import')}</button>
              <button type="button" className="btn danger" onClick={() => void clearAll()}>{t('settings.clear')}</button>
              <span className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{t('settings.stats', { ...counts, size: formatBytes(usage) })}</span>
            </div>
            <ol className={s.promises}>
              <li>{t('settings.promise1')}</li>
              <li>{t('settings.promise2')}</li>
              <li>{t('settings.promise3')}</li>
            </ol>
            <CrashLog />
            <div className={s.dataRow}>
              <Link to="/setup" className="btn quiet">{t('settings.rerunSetup')}</Link>
              <span className="muted" style={{ fontSize: 12 }}>{t('settings.rerunSetupHint')}</span>
            </div>
            <Link to="/about#privacy" className="muted" style={{ fontSize: 13, alignSelf: 'flex-start' }}>{t('settings.aboutLink')} →</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
